import { ChangeDetectorRef, Component, Input, IterableDiffers, SimpleChanges } from '@angular/core';
import { ElementAttribute, ElementTagName } from '@models/editor';
import { defaultNumberInputArrowedKey, KeyFilterSerivce } from '@service/key-filter.service';
import { UndoManagerService } from '@service/canvas/undoMgr.service';
import { mixedString } from 'icreate/common/models/mimic';

@Component({
    selector: 'app-appearance-options',
    templateUrl: './appearance-options.component.html',
    styleUrls: ['./../design-side-menu/design-side-menu.component.css'],
    standalone: false,
})
export class AppearanceOptionsComponent {
    readonly ALLOWED_KEYS = defaultNumberInputArrowedKey;
    readonly OPACITY_MAX: number = 100;
    readonly OPACITY_MIN: number = 0;
    readonly Y_POSITION_OFFSET_FOR_SCREEN_SIZE: number = 210;
    readonly MIXED_STRING = mixedString;

    @Input() selectedElements;

    isStrokeHidden: boolean = false;
    isFillHidden: boolean = false;
    shouldHideOpacity: boolean = false;

    fillHidden: boolean = false;
    hasFillAttribute: boolean = false;
    fillColor: string;

    strokeHidden: boolean = false;
    hasStrokeAttribute: boolean = false;
    strokeColor: string;

    opacity: string;

    public iterableDiffer;
    private oldFillColors = new Map<SVGElement, string>();
    private oldStrokeColors = new Map<SVGElement, string>();

    constructor(
        private iterableDiffers: IterableDiffers,
        private cdr: ChangeDetectorRef,
        private keyFilterService: KeyFilterSerivce,
        private undoManagerService: UndoManagerService
    ) {
        this.iterableDiffer = iterableDiffers.find([]).create(null);
    }

    ngDoCheck() {
        let changes = this.iterableDiffer.diff(this.selectedElements);
        if (changes) {
            this.shouldHideStroke();
            this.shouldHideFill();
            this.hasFillAttribute = false;
            this.fillColor = undefined;
            this.hasStrokeAttribute = false;
            this.strokeColor = undefined;
            this.fillHidden = false;
            this.cdr.detectChanges();

            if (this.selectedElements.length > 1) {
                this.opacity = mixedString;
                return;
            } else {
                if (this.connectorType) {
                    const fillElement = this.selectedElements[0].querySelector('[isfill="true"]') as HTMLElement;
                    const strokeElement = this.selectedElements[0].querySelector('[isstroke="true"]') as HTMLElement;

                    const fillHidden = fillElement?.getAttribute(ElementAttribute.fillOpacity);
                    const fill = fillElement?.getAttribute(ElementAttribute.fill);
                    this.fillHidden = fillHidden == '0';
                    this.hasFillAttribute = !!fill;
                    this.fillColor = fill;

                    const strokeHidden = strokeElement?.getAttribute(ElementAttribute.strokeOpacity);
                    const stroke = strokeElement?.getAttribute(ElementAttribute.stroke);
                    this.hasStrokeAttribute = !!stroke;
                    this.strokeColor = stroke;
                    this.strokeHidden = strokeHidden == '0';
                    return;
                }
                let opacity = this.selectedElements[0].getAttribute(ElementAttribute.opacity);
                const fill = this.selectedElements[0].getAttribute(ElementAttribute.fill);
                const stroke = this.selectedElements[0].getAttribute(ElementAttribute.stroke);
                const fillHidden = this.selectedElements[0].getAttribute(ElementAttribute.fillOpacity);
                const strokeHidden = this.selectedElements[0].getAttribute(ElementAttribute.strokeOpacity);
                const shouldHideOpacity = this.selectedElements[0].getAttribute(ElementAttribute.opacityPreviewOption);

                this.shouldHideOpacity = Boolean(JSON.parse(shouldHideOpacity));
                this.fillHidden = fillHidden == 0;
                this.strokeHidden = strokeHidden == 0;

                if (this.shouldHideOpacity) {
                    if (!opacity) this.opacity = this.OPACITY_MAX.toString();
                    else {
                        opacity = this.selectedElements[0].getAttribute(ElementAttribute.opacityPreview);
                        this.opacity = (Number(opacity) * 100).toString();
                    }
                } else {
                    if (!opacity) this.opacity = this.OPACITY_MAX.toString();
                    else {
                        this.opacity = (Number(opacity) * 100).toString();
                    }
                    this.selectedElements[0].setAttribute(
                        ElementAttribute.opacity,
                        (Number(this.opacity) / 100).toString()
                    );
                }

                if (fill) {
                    this.hasFillAttribute = true;
                    this.fillColor = fill;
                }

                if (stroke) {
                    this.hasStrokeAttribute = true;
                    this.strokeColor = stroke;
                }
            }
        }

        if (this.isStrokeHidden || this.isFillHidden) {
            const colorPicker = document.getElementsByTagName('color-picker')[0] as HTMLElement;

            if (colorPicker) {
                const prevSibling = colorPicker?.previousElementSibling;
                const { y } = prevSibling.getBoundingClientRect();
                if (colorPicker.children[0] && this.isStrokeHidden) {
                    (colorPicker.children[0] as HTMLElement).style.top =
                        `${y - this.Y_POSITION_OFFSET_FOR_SCREEN_SIZE}px`;
                } else {
                    (colorPicker.children[0] as HTMLElement).style.top = `${y}px`;
                }
            }
        }
    }

    handleOpacityChange({ name, value }) {
        this.selectedElements.forEach(element => {
            if (this.connectorType) return;
            const opacityOption = element.getAttribute(ElementAttribute.opacityPreviewOption) || 'false';
            const opacity = (Number(value) / 100).toString();
            if (!Boolean(JSON.parse(opacityOption))) {
                element.setAttribute(ElementAttribute.opacity, opacity);
            } else {
                element.setAttribute(ElementAttribute.opacityPreview, opacity);
            }
        });
    }

    opacityIconClick() {
        const elementOptionShouldHideOpacity = !this.shouldHideOpacity;
        this.selectedElements.forEach(element => {
            const opacity = element.getAttribute(ElementAttribute.opacity);
            const opacityPreview = element.getAttribute(ElementAttribute.opacityPreview) || 0;
            element.setAttribute(ElementAttribute.opacityPreviewOption, elementOptionShouldHideOpacity);
            element.setAttribute(ElementAttribute.opacityPreview, opacity);
            element.setAttribute(ElementAttribute.opacity, this.shouldHideOpacity ? opacityPreview : 0);
        });
        this.shouldHideOpacity = !this.shouldHideOpacity;
    }

    fillIconClick() {
        this.selectedElements.forEach(element => {
            this.hiddenFillAttribute(element, this.fillHidden);
        });
        this.fillHidden = !this.fillHidden;
    }

    addFillColor() {
        this.hasFillAttribute = true;
        this.selectedElements.forEach(element => {
            const connectorType = element.getAttribute(ElementAttribute.connectorType);
            if (connectorType) {
                const fillElements = element.querySelectorAll('[isfill="true"]');
                fillElements.forEach(element => {
                    element.setAttribute(ElementAttribute.fill, '#ffffff');
                });
            } else element.setAttribute(ElementAttribute.fill, '#ffffff');
        });
    }

    deleteFill() {
        this.selectedElements.forEach(element => {
            this.deleteFillAttribute(element);
        });
        this.hasFillAttribute = false;
    }

    appendFF(color: string | undefined): string {
        if (color?.length === 7) return color + 'ff';
        if (color?.length === 9) return color;
        return color;
    }

    handleFillMouseUp = () => {
        this.selectedElements.forEach(element => {
            const oldColor = this.appendFF(this.oldFillColors.get(element));
            const currentColor = element.getAttribute(ElementAttribute.fill);

            if (oldColor !== undefined && oldColor !== currentColor) {
                this.undoManagerService.addCommandToHistory(
                    element,
                    { [ElementAttribute.fill]: oldColor },
                    'Fill color changed'
                );
            }
        });
        this.oldFillColors.clear();
        window.removeEventListener('mouseup', this.handleFillMouseUp);
    };

    handleFillChange(newColor) {
        this.selectedElements.forEach(element => {
            if (!this.oldFillColors.has(element)) {
                const oldColor = element.getAttribute(ElementAttribute.fill);
                this.oldFillColors.set(element, oldColor || '');
            }
            this.setFillAttribute(element, newColor);
        });
        window.addEventListener('mouseup', this.handleFillMouseUp);
    }

    strokeIconClick() {
        this.selectedElements.forEach(element => {
            this.hiddenStrokeAttribute(element, this.strokeHidden);
        });
        this.strokeHidden = !this.strokeHidden;
    }

    addStroke() {
        this.hasStrokeAttribute = true;
        this.selectedElements.forEach(element => {
            const connectorType = element.getAttribute(ElementAttribute.connectorType);
            if (connectorType) {
                const strokeElements = element.querySelectorAll('[isstroke="true"]');
                strokeElements.forEach(element => {
                    element.setAttribute(ElementAttribute.stroke, '#000000');
                });
            } else element.setAttribute(ElementAttribute.stroke, '#000000');
        });
    }

    deleteStroke() {
        this.selectedElements.forEach(element => {
            this.deleteStrokeAttribute(element);
        });
        this.hasStrokeAttribute = false;
    }

    handleStrokeMouseUp = () => {
        this.selectedElements.forEach(element => {
            const oldColor = this.appendFF(this.oldStrokeColors.get(element));
            const currentColor = element.getAttribute(ElementAttribute.stroke);

            if (oldColor !== undefined && oldColor !== currentColor) {
                this.undoManagerService.addCommandToHistory(
                    element,
                    { [ElementAttribute.stroke]: oldColor },
                    'Stroke color changed'
                );
            }
        });
        this.oldStrokeColors.clear();
        window.removeEventListener('mouseup', this.handleStrokeMouseUp);
    };

    handleStrokeChange(newColor: string) {
        this.selectedElements.forEach(element => {
            if (!this.oldStrokeColors.has(element)) {
                const oldColor = element.getAttribute(ElementAttribute.stroke);
                this.oldStrokeColors.set(element, oldColor || '');
            }

            this.setStrokeAttribute(element, newColor);
        });
        window.addEventListener('mouseup', this.handleStrokeMouseUp);
    }

    hiddenFillAttribute(element: any, fillHidden: boolean) {
        if (this.connectorType) {
            const fillElements = element.querySelectorAll('[isfill="true"]');
            fillElements.forEach(element => {
                element.setAttribute(ElementAttribute.fillOpacity, fillHidden ? 1 : 0);
            });
            return;
        }

        element.setAttribute(ElementAttribute.fillOpacity, fillHidden ? 1 : 0);
        if (element.tagName === ElementTagName.g && element.childNodes) {
            element.childNodes.forEach(childElement => {
                if (childElement.nodeType === 1) {
                    this.hiddenFillAttribute(childElement, fillHidden);
                }
            });
        }
    }

    deleteFillAttribute(element: any) {
        if (this.connectorType) {
            const fillElements = element.querySelectorAll('[isfill="true"]');
            fillElements.forEach(element => {
                element.setAttribute(ElementAttribute.fill, 'none');
            });
            return;
        }

        element.setAttribute(ElementAttribute.fill, 'none');
        if (element.tagName === ElementTagName.g && element.childNodes) {
            element.childNodes.forEach(childElement => {
                if (childElement.nodeType === 1) {
                    this.deleteFillAttribute(childElement);
                }
            });
        }
    }

    setFillAttribute(element: any, newColor: string) {
        if (this.connectorType) {
            const fillElements = element.querySelectorAll('[isfill="true"]');
            fillElements.forEach(element => {
                element.setAttribute(ElementAttribute.fill, newColor);
            });
            return;
        }

        element.setAttribute(ElementAttribute.fill, newColor);
        if (element.tagName === ElementTagName.g && element.childNodes) {
            element.childNodes.forEach(childElement => {
                if (childElement.nodeType === 1) {
                    this.setFillAttribute(childElement, newColor);
                }
            });
        }
    }

    hiddenStrokeAttribute(element: any, strokeHidden: boolean) {
        if (this.connectorType) {
            const strokeElements = element.querySelectorAll('[isstroke="true"]');
            strokeElements.forEach(element => {
                if (!element.getAttribute('data-bbox-ignore')) {
                    element.setAttribute(ElementAttribute.strokeOpacity, strokeHidden ? 1 : 0);
                }
            });
            return;
        }

        element.setAttribute(ElementAttribute.strokeOpacity, strokeHidden ? 1 : 0);
        if (element.tagName === ElementTagName.g && element.childNodes) {
            element.childNodes.forEach(childElement => {
                if (childElement.nodeType === 1) {
                    this.hiddenStrokeAttribute(childElement, strokeHidden);
                }
            });
        }
    }

    deleteStrokeAttribute(element: any) {
        if (this.connectorType) {
            const strokeElements = element.querySelectorAll('[isstroke="true"]');
            strokeElements.forEach(element => {
                if (!element.getAttribute('data-bbox-ignore')) {
                    element.setAttribute(ElementAttribute.stroke, 'none');
                }
            });
            return;
        } else {
        }

        element.setAttribute(ElementAttribute.stroke, 'none');
        if (element.tagName === ElementTagName.g && element.childNodes) {
            element.childNodes.forEach(childElement => {
                if (childElement.nodeType === 1) {
                    childElement.setAttribute(ElementAttribute.stroke, 'none');
                }
            });
        }
    }

    setStrokeAttribute(element: any, newColor: string) {
        if (this.connectorType) {
            const strokeElements = element.querySelectorAll('[isstroke="true"]');
            strokeElements.forEach(element => {
                element.setAttribute(ElementAttribute.stroke, newColor);
            });
            return;
        }

        element.setAttribute(ElementAttribute.stroke, newColor);
        if (element.tagName === ElementTagName.g && element.childNodes) {
            element.childNodes.forEach(childElement => {
                if (childElement.nodeType === 1) {
                    this.setStrokeAttribute(childElement, newColor);
                }
            });
        }
    }

    shouldHideStroke() {
        if (this.selectedElements.length === 1) {
            if (this.selectedElements[0].tagName === ElementTagName.text) {
                this.isStrokeHidden = true;
                return;
            }
        }
        this.isStrokeHidden = false;
    }

    shouldHideFill() {
        if (this.selectedElements.length === 1) {
            if (this.selectedElements[0].tagName === ElementTagName.line) {
                this.isFillHidden = true;
                return;
            }
        }
        this.isFillHidden = false;
    }

    get connectorType() {
        if (!this.selectedElements || this.selectedElements.length != 1) return false;
        const element = this.selectedElements[0];
        const connectorType = element.getAttribute(ElementAttribute.connectorType);
        return connectorType;
    }

    validSpacingKeyDown(eventObj: { event: KeyboardEvent; value }) {
        const { event, value } = eventObj;
        const shouldBlockKey = this.keyFilterService.shouldPreventDefault(event, this.ALLOWED_KEYS);
        if (shouldBlockKey) event.preventDefault();
    }
}
